import { useCallback, useEffect } from 'react';

import { useEnvironment } from '@wix/yoshi-flow-editor';

import { useAppSelector } from '@/store';
import { selectOneChatSettings } from '@/features';
import { EXPERIMENTS, SS_REPORTED_NEW_VISITOR } from '@/constants';
import { CurrentStatus } from '@/features/oneChatSettings/model';

import {
  useRootProps,
  useWidgetServices,
} from 'AiAssistantWidget/Widget/contexts';
import { useIsExperimentEnabled } from 'AiAssistantWidget/experiments';

const REPORT_NEW_VISITOR_INTERVAL_MS = 5 * 60 * 1000;

const useSSReportedNewVisitor = (reportEvery5Min: boolean) => {
  const isReported = useCallback(() => {
    const storedValue = sessionStorage.getItem(SS_REPORTED_NEW_VISITOR);

    if (!reportEvery5Min) {
      return (
        storedValue === 'true' ||
        (storedValue !== null && Number.isFinite(Number(storedValue)))
      );
    }

    const reportedAt = Number(storedValue);

    return (
      Number.isFinite(reportedAt) &&
      Date.now() - reportedAt < REPORT_NEW_VISITOR_INTERVAL_MS
    );
  }, [reportEvery5Min]);

  const setReported = useCallback((value: string | null) => {
    if (value === null) {
      sessionStorage.removeItem(SS_REPORTED_NEW_VISITOR);
      return;
    }

    sessionStorage.setItem(SS_REPORTED_NEW_VISITOR, value);
  }, []);

  return { isReported, setReported };
};

export const useReportNewVisitor = () => {
  const { visitorPresenceService } = useWidgetServices();
  const { isEditor, isSSR } = useEnvironment();
  const { page: { id: pageId, name: pageName } = {} } = useRootProps();
  const isReportNewVisitorExperimentEnabled = useIsExperimentEnabled(
    EXPERIMENTS.REPORT_NEW_VISITOR,
  );
  const isReportNewVisitorEvery5MinExperimentEnabled = useIsExperimentEnabled(
    EXPERIMENTS.REPORT_NEW_VISITOR_EVERY_5_MIN,
  );

  const currentStatus = useAppSelector(selectOneChatSettings.currentStatus);
  const isCurrentStatusOnline =
    currentStatus === CurrentStatus.AI_ASSISTANT_ONLINE ||
    currentStatus === CurrentStatus.MANUAL_ONLINE;

  const { isReported, setReported } = useSSReportedNewVisitor(
    isReportNewVisitorEvery5MinExperimentEnabled,
  );

  useEffect(() => {
    if (isEditor || isSSR) {
      return;
    }
    if (isReported()) {
      return;
    }
    if (!isCurrentStatusOnline) {
      return;
    }
    if (!isReportNewVisitorExperimentEnabled) {
      return;
    }
    if (!pageId || !pageName) {
      return;
    }

    const reportMarker = isReportNewVisitorEvery5MinExperimentEnabled
      ? Date.now().toString()
      : 'true';
    setReported(reportMarker);
    void visitorPresenceService
      .reportNewVisitor({ pageId, pageName })
      .catch(() => {
        if (sessionStorage.getItem(SS_REPORTED_NEW_VISITOR) === reportMarker) {
          setReported(null);
        }
      });
  }, [
    isEditor,
    isSSR,
    isCurrentStatusOnline,
    isReportNewVisitorExperimentEnabled,
    isReportNewVisitorEvery5MinExperimentEnabled,
    pageId,
    pageName,
    visitorPresenceService,
    isReported,
    setReported,
  ]);
};
